import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import MasonryGrid from "../components/MasonryGrid";
import PhotoDrawer from "../components/PhotoDrawer";
import Footer from "../components/Footer";
import {
  getFavorites,
  getCollections,
  deleteCollection,
  renameCollection,
  getCollectionPhotos,
  removePhotoFromCollection,
  getDownloads,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Heart, FolderOpen, ArrowLeft, Trash2, Pencil, Check, X, Download,
} from "lucide-react";

const DownloadIco = Download;

const TABS = [
  { key: "favorites", label: "Favorites", icon: Heart },
  { key: "collections", label: "Collections", icon: FolderOpen },
  { key: "downloads", label: "Downloads", icon: DownloadIco },
];

export default function Library() {
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const { isAuthed, loading, openAuth } = useAuth();

  const tab = searchParams.get("tab") || "favorites";
  const collectionId = searchParams.get("collection");
  const openId = searchParams.get("photo");

  const [favorites, setFavorites] = useState([]);
  const [collections, setCollections] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [collPhotos, setCollPhotos] = useState([]);
  const [collMeta, setCollMeta] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [initialPhoto, setInitialPhoto] = useState(null);

  // Redirect guests
  useEffect(() => {
    if (!loading && !isAuthed) {
      openAuth("login", () => nav("/dashboard/library"));
      nav("/", { replace: true });
    }
  }, [loading, isAuthed, openAuth, nav]);

  const loadFavorites = useCallback(async () => {
    setLoadingData(true);
    try { setFavorites(await getFavorites()); } finally { setLoadingData(false); }
  }, []);
  const loadCollections = useCallback(async () => {
    setLoadingData(true);
    try { setCollections(await getCollections()); } finally { setLoadingData(false); }
  }, []);
  const loadDownloads = useCallback(async () => {
    setLoadingData(true);
    try { setDownloads(await getDownloads()); } finally { setLoadingData(false); }
  }, []);
  const loadCollectionPhotos = useCallback(async (id) => {
    setLoadingData(true);
    try {
      const { collection, photos } = await getCollectionPhotos(id);
      setCollMeta(collection);
      setCollPhotos(photos);
    } finally { setLoadingData(false); }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    if (tab === "favorites") loadFavorites();
    else if (tab === "collections" && !collectionId) loadCollections();
    else if (tab === "collections" && collectionId) loadCollectionPhotos(collectionId);
    else if (tab === "downloads") loadDownloads();
  }, [tab, collectionId, isAuthed, loadFavorites, loadCollections, loadCollectionPhotos, loadDownloads]);

  const setTab = (key) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", key);
    p.delete("collection");
    p.delete("photo");
    setSearchParams(p);
  };
  const openCollection = (id) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", "collections");
    p.set("collection", id);
    p.delete("photo");
    setSearchParams(p);
  };
  const backToCollections = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("collection");
    p.delete("photo");
    setSearchParams(p);
  };

  const openPhoto = (p) => nav(`/dashboard/photo/${p.id}`);
  const closeDrawer = () => {};

  const heroTitle = useMemo(() => {
    if (tab === "collections" && collMeta) return collMeta.name;
    return "My library";
  }, [tab, collMeta]);

  if (loading || !isAuthed) {
    return (
      <div className="min-h-screen bg-background">
        <Header compact />
        <div className="pt-32 text-center text-sm text-muted-foreground">Loading library…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header compact />
      <div className="pt-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10">
          <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground">
            {tab === "collections" && collMeta ? "Collection" : "Library"}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink mt-2" data-testid="library-title">
            {heroTitle}
          </h1>

          {!(tab === "collections" && collMeta) && (
            <div className="mt-8 flex gap-1 border-b border-border" data-testid="library-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`library-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors ${
                      active
                        ? "border-b-2 border-ink text-ink -mb-px font-medium"
                        : "text-muted-foreground hover:text-ink"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? "text-gold" : ""}`} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}

          {tab === "collections" && collMeta && (
            <button
              onClick={backToCollections}
              data-testid="collection-back-btn"
              className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-ink"
            >
              <ArrowLeft className="h-4 w-4" /> Back to collections
            </button>
          )}
        </div>

        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 pb-24">
          {tab === "favorites" && (
            <FavoritesView photos={favorites} onOpen={openPhoto} loading={loadingData} />
          )}
          {tab === "collections" && !collectionId && (
            <CollectionsGrid
              collections={collections}
              loading={loadingData}
              onOpen={openCollection}
              onDeleted={loadCollections}
              onRenamed={loadCollections}
            />
          )}
          {tab === "collections" && collectionId && (
            <CollectionPhotosView
              collection={collMeta}
              photos={collPhotos}
              onOpen={openPhoto}
              onRemoved={() => loadCollectionPhotos(collectionId)}
              loading={loadingData}
            />
          )}
          {tab === "downloads" && (
            <DownloadsView downloads={downloads} loading={loadingData} onOpen={openPhoto} />
          )}
        </section>
      </div>

      <PhotoDrawer
        open={Boolean(openId)}
        photoId={openId}
        initialPhoto={initialPhoto && String(initialPhoto.id) === String(openId) ? initialPhoto : null}
        onClose={closeDrawer}
        onOpenPhoto={openPhoto}
      />

      <Footer />
    </div>
  );
}

function FavoritesView({ photos, onOpen, loading }) {
  return (
    <MasonryGrid
      photos={photos}
      onOpen={onOpen}
      loading={loading}
      hasMore={false}
      testId="favorites-masonry"
      emptyState={
        !loading && photos.length === 0 ? (
          <div data-testid="favorites-empty" className="py-24 text-center max-w-md mx-auto">
            <div className="font-serif text-3xl text-ink">No favorites yet.</div>
            <p className="text-muted-foreground mt-3 text-sm">
              Tap the heart on any photo to save it here.
            </p>
          </div>
        ) : null
      }
    />
  );
}

function CollectionsGrid({ collections, loading, onOpen, onDeleted, onRenamed }) {
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const startRename = (c) => { setRenaming(c.id); setRenameValue(c.name); };
  const commitRename = async (c) => {
    if (renameValue.trim() && renameValue !== c.name) {
      await renameCollection(c.id, renameValue.trim());
      onRenamed();
    }
    setRenaming(null);
  };
  const doDelete = async (c) => {
    if (!window.confirm(`Delete collection "${c.name}"?`)) return;
    await deleteCollection(c.id);
    onDeleted();
  };

  if (loading) return <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!collections.length) {
    return (
      <div data-testid="collections-empty" className="py-24 text-center max-w-md mx-auto">
        <div className="font-serif text-3xl text-ink">No collections yet.</div>
        <p className="text-muted-foreground mt-3 text-sm">
          Open any photo and use the Collections button to create one.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="collections-grid">
      {collections.map((c) => (
        <div
          key={c.id}
          data-testid={`collection-card-${c.id}`}
          className="group relative bg-white border border-border overflow-hidden"
        >
          <button
            onClick={() => onOpen(c.id)}
            className="block w-full text-left"
            data-testid={`collection-open-${c.id}`}
          >
            <div
              className="w-full aspect-[4/3] bg-secondary"
              style={{ backgroundColor: c.cover?.avg_color || "#e9e5dd" }}
            >
              {c.cover?.src?.medium && (
                <img
                  src={c.cover.src.medium}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              )}
            </div>
          </button>
          <div className="p-4 flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {renaming === c.id ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-border outline-none focus:border-ink"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(c);
                      if (e.key === "Escape") setRenaming(null);
                    }}
                  />
                  <button onClick={() => commitRename(c)} className="h-7 w-7 inline-flex items-center justify-center hover:bg-secondary">
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setRenaming(null)} className="h-7 w-7 inline-flex items-center justify-center hover:bg-secondary">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="font-serif text-lg text-ink truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.photo_count} photos</div>
                </>
              )}
            </div>
            {renaming !== c.id && (
              <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => startRename(c)}
                  data-testid={`collection-rename-btn-${c.id}`}
                  className="h-8 w-8 inline-flex items-center justify-center hover:bg-secondary"
                  title="Rename"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => doDelete(c)}
                  data-testid={`collection-delete-btn-${c.id}`}
                  className="h-8 w-8 inline-flex items-center justify-center hover:bg-secondary text-destructive"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CollectionPhotosView({ collection, photos, onOpen, onRemoved, loading }) {
  if (loading) return <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!photos.length) {
    return (
      <div className="py-24 text-center max-w-md mx-auto">
        <div className="font-serif text-3xl text-ink">Empty collection.</div>
        <p className="text-muted-foreground mt-3 text-sm">
          Open any photo, click Collections, and add it here.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-6" data-testid="collection-meta">
        {collection?.photo_count ?? photos.length} photos
      </div>
      <MasonryGrid
        photos={photos}
        onOpen={onOpen}
        loading={false}
        hasMore={false}
        testId="collection-masonry"
      />
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="collection-remove-grid">
        {photos.map((p) => (
          <div key={`rm-${p.id}`} className="flex items-center gap-2 p-2 border border-border bg-white text-xs">
            <img src={p.src?.tiny || p.src?.small} alt="" className="h-10 w-10 object-cover" />
            <div className="flex-1 min-w-0 truncate">{p.photographer?.name}</div>
            <button
              data-testid={`collection-remove-photo-${p.id}`}
              onClick={async () => { await removePhotoFromCollection(collection.id, p.id); onRemoved(); }}
              className="h-7 w-7 inline-flex items-center justify-center hover:bg-secondary text-destructive"
              title="Remove from collection"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DownloadsView({ downloads, loading, onOpen }) {
  if (loading) return <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!downloads.length) {
    return (
      <div data-testid="downloads-empty" className="py-24 text-center max-w-md mx-auto">
        <div className="font-serif text-3xl text-ink">No downloads yet.</div>
        <p className="text-muted-foreground mt-3 text-sm">Download any photo to see it listed here.</p>
      </div>
    );
  }
  return (
    <div className="border border-border bg-white divide-y divide-border" data-testid="downloads-list">
      {downloads.map((d) => (
        <button
          key={d.id}
          onClick={() => onOpen(d.photo)}
          data-testid={`download-history-row-${d.id}`}
          className="w-full flex items-center gap-4 p-3 hover:bg-secondary text-left"
        >
          <img
            src={d.photo?.src?.small || d.photo?.src?.tiny}
            alt=""
            className="h-14 w-20 object-cover shrink-0"
            style={{ backgroundColor: d.photo?.avg_color || "#e9e5dd" }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-ink truncate">{d.photo?.alt || `Photo ${d.photo_id}`}</div>
            <div className="text-xs text-muted-foreground">
              by {d.photo?.photographer?.name} · {d.photo?.width}×{d.photo?.height}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground shrink-0">{d.size}</div>
          <div className="text-xs text-muted-foreground shrink-0 hidden sm:block">
            {new Date(d.downloaded_at).toLocaleString()}
          </div>
        </button>
      ))}
    </div>
  );
}
